import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { RootState } from '../redux/store';
import { useDispatch, useSelector } from '../redux/hooks';
import { openLogin } from '../redux/slices/modalSlice';

const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  padding: 40px;
  background: #ffffff;
`;

const Title = styled.h1`
  font-family: 'IBM Plex Mono', monospace;
  font-size: 32px;
  color: #a13b16;
  text-align: center;
  margin-bottom: 30px;
`;

const FlexRow = styled.div`
  display: flex;
  gap: 40px;
  align-items: flex-start;
`;

const AuthorsList = styled.div`
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-content: flex-start;
  row-gap: 10px;
`;

const ListItem = styled.div`
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;
  background: #f8f8f8;
  white-space: nowrap;

  &:hover {
    background: #f0e6e1;
  }
`;

const Form = styled.form`
  flex: 0 0 300px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Input = styled.input`
  padding: 10px;
  border-radius: 8px;
  border: 1px solid #ccc;
  font-size: 16px;
`;

const TextArea = styled.textarea`
  padding: 10px;
  border-radius: 8px;
  border: 1px solid #ccc;
  font-size: 16px;
  resize: vertical;
  min-height: 80px;
`;

const Button = styled.button`
  padding: 10px;
  border: none;
  border-radius: 8px;
  background: #a13b16;
  color: white;
  font-weight: bold;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #8b2f0f;
  }
`;

const DangerButton = styled(Button)`
  background: #c0392b;

  &:hover {
    background: #a93226;
  }
`;

const CancelButton = styled(Button)`
  background: #999;

  &:hover {
    background: #777;
  }
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const BASE_API_URL = process.env.REACT_APP_BASE_API_URL;

interface Author {
  author_id: number;
  author_name: string;
  author_description?: string;
}

const AuthorsListView: React.FC = () => {
  const [authors, setAuthors] = useState<Author[]>([]);

  useEffect(() => {
    fetch(`${BASE_API_URL}/authors`)
      .then(res => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then(data => {
        setAuthors(data);
      })
      .catch(err => {
        console.error('Failed to fetch authors:', err);
      });
  }, []);

  const accessToken = useSelector((state: RootState) => state.auth.accessToken);
  const [selectedAuthor, setSelectedAuthor] = useState<Author | null>(null);
  const [nameInput, setNameInput] = useState('');
  const [descInput, setDescInput] = useState('');
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleSelect = useCallback((author: Author) => {
    setSelectedAuthor(author);
    setNameInput(author.author_name);
    setDescInput(author.author_description || '');
  }, []);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!nameInput.trim()) return;

      if (selectedAuthor) {
        fetch(
          `${BASE_API_URL}/authors/${selectedAuthor.author_id}`, 
          {
            method: "PUT", 
            credentials: 'include',
            headers: { 
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({ 
              author_name: nameInput, 
              author_description: descInput 
            })
          }
        )
        .then(response => {
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            return response.json();
        })
        .then(data => {
          setAuthors(prev =>
            prev.map(a =>
              a.author_id === selectedAuthor.author_id
              ? data
              : a
            )
          );
        })
        .catch(error => {
            console.error('Error during PUT request:', error);
        });
      } else {
        if (!accessToken) {
          console.error('Access token is missing.');
          dispatch(openLogin());
          return;
        }
        fetch(
          `${BASE_API_URL}/authors`, 
          {
            method: "POST", 
            credentials: 'include',
            headers: { 
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({ 
              author_name: nameInput, 
              author_description: descInput 
            })
          }
        )
        .then(response => {
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            return response.json();
        })
        .then(data => {
          setAuthors(prev => [...prev, data]);
        })
        .catch(error => {
            console.error('Error during POST request:', error);
        });
      }

      setSelectedAuthor(null);
      setNameInput('');
      setDescInput('');
      },
      [nameInput, selectedAuthor, accessToken, descInput, dispatch]
    );

  const handleDelete = useCallback(() => {
    if (!selectedAuthor) return;
    
    fetch(
      `${BASE_API_URL}/authors/${selectedAuthor.author_id}`, 
      {
        method: "DELETE", 
        credentials: 'include',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        }
      }
    )
    .then(response => {
        if (!response.ok) {
            throw new Error('Network response was not ok');
        }

        setAuthors(prev =>
          prev.filter(a => a.author_id !== selectedAuthor.author_id)
        );

        return response.json();
    })
    .catch(error => {
        console.error('Error during DELETE request:', error);
    });

    setSelectedAuthor(null);
    setNameInput('');
    setDescInput('');
  }, [accessToken, selectedAuthor]);

  const handleCancel = useCallback(() => {
    setSelectedAuthor(null);
    setNameInput('');
    setDescInput('');
  }, []);

  return (
    <PageContainer>
      <TopBar>
        <Title>Автори</Title>
        <Button onClick={() => navigate('/admin')}>Головна</Button>
      </TopBar>
      <FlexRow>
        <AuthorsList>
          {authors.map(author => (
            <ListItem key={author.author_id} onClick={() => handleSelect(author)}>
              {author.author_name}
            </ListItem>
          ))}
        </AuthorsList>

        <Form onSubmit={handleSubmit}>
          <Input
            placeholder="Ім'я автора"
            value={nameInput}
            onChange={e => setNameInput(e.target.value)}
          />
          <TextArea
            placeholder="Опис автора (необов'язково)"
            value={descInput}
            onChange={e => setDescInput(e.target.value)}
          />
          <Button type="submit">{selectedAuthor ? 'Зберегти' : 'Створити'}</Button>
          {selectedAuthor && (
            <>
              <DangerButton type="button" onClick={handleDelete}>Видалити</DangerButton>
              <CancelButton type="button" onClick={handleCancel}>Відмінити</CancelButton>
            </>
          )}
        </Form>
      </FlexRow>
    </PageContainer>
  );
};

export default AuthorsListView;
